/**
 * Articles (/dashboard/admin/articles, /dashboard/admin/articles/<id>): On My Block, written and published by Chicago
 * Politics staff (worker/src/block-admin.js; the public pages are worker/src/block.js). Not designed: built from the
 * Access/Approvals parts (page head, tabs, cards, list rows, chips, buttons, text inputs).
 *
 *   Home    Suggested: recently passed ordinances no article covers yet (Draft with AI · Blank). Articles: every
 *           draft and published article.
 *   Editor  Title, dek and the body in the rich editor (ArticleEditor.tsx; or as Markdown), with AI (Improve / Shorten the
 *           selection, Headlines) and Preview. The
 *           side: Publish (status, URL, byline), Places (neighborhoods, wards, records), Image (upload), Sources
 *           (a record's meetings: verbatim quotes and clips to insert, or one alderperson in one meeting).
 * Quotes and clips are inserted exactly as the transcript has them (the AI draft does the same server-side).
 */
import { useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { BarChart3, ExternalLink, FileText, ImagePlus, Quote, Sparkles, Trash2, Video } from "lucide-react";
import { List, ListRow, RowCell, RowMain, RowSub, RowTitle } from "../components/shared/List";
import { toastStore } from "../stores/toastStore";
import { errorText } from "../lib/apiError";
import { ArticleEditor, OMB_DRAG, type EditorHandle, type ExistingClip } from "./ArticleEditor";
import { VideoModal, type VideoPick } from "./VideoModal";
import { ChartPicker, parseChartSpec, type ChartSpec } from "./ChartPicker";
import { useCollab } from "./collab";

const API = "/api/dashboard/articles";

export interface ArticleSummary {
  id: string; slug: string; status: "draft" | "published"; title: string; dek: string;
  neighborhoods: string[]; wards: number[]; records: string[]; hero: { url: string; alt: string; credit: string } | null;
  author: string; createdAt: string; updatedAt: string; publishedAt: string | null; readMinutes: number; words?: number;
  /** The live record's actions after the article was last edited (block-admin.js staleness): it may be out of date. */
  updates?: { record: string; date: string; action: string; text: string | null }[];
  /** Page views in the last 28 days (GA4 via Admin → Traffic, worker/src/traffic.js): null for drafts and before the first pull. */
  views28d?: number | null;
}
export interface Article extends ArticleSummary { body: string }
interface Neighborhood { slug: string; name: string; alsoKnownAs: string[]; wards: number[] }
interface Suggestion {
  record: string; matterId: string; title: string; fullTitle: string; category: string; actionDate: string; meetingId: string | null;
  sponsor: { name: string; ward: number | null } | null; divided: boolean; wards: number[]; neighborhoods: { slug: string; name: string }[];
}
interface QuoteSrc { meetingId: string; t: number; speakerId: string; speaker: string; ward: number | null; text: string }
interface ClipSrc { meetingId: string; start: number; end: number; speakerId: string; speaker: string; caption: string }
interface MeetingSrc {
  meetingId: string; body: string | null; date: string | null; vimeo: string | null; thumbnail: string | null;
  speakers: { ward: number; name: string; moments: { t: number; summary: string; stance: string | null }[] }[];
  quotes: QuoteSrc[]; clips: ClipSrc[];
}
interface Sources {
  matter: { id: string; record: string; title: string; nickname: string | null; action: string | null; actionDate: string | null; sponsor: string | null; sponsorWard: number | null };
  summary: { summary?: string } | null; wards: number[]; neighborhoods: { slug: string; name: string }[]; meetings: MeetingSrc[];
}

async function call<T>(path: string, method = "GET", body?: unknown, raw?: Blob): Promise<T | null> {
  const res = await fetch(path, {
    method, credentials: "include",
    headers: raw ? { "content-type": raw.type } : body ? { "content-type": "application/json" } : undefined,
    body: raw ?? (body ? JSON.stringify(body) : undefined),
  }).catch(() => null);
  const data = res ? await res.json().catch(() => null) : null;
  if (!res?.ok) toastStore.getState().show({ message: errorText(data) ?? "Couldn't do that. Try again." });
  return res?.ok ? (data as T) : null;
}

const day = (iso: string | null | undefined) => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");
const clock = (sec: number) => {
  const s = Math.max(0, Math.floor(sec)), h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}` : `${m}:${String(r).padStart(2, "0")}`;
};
const meetingLabel = (m: { body: string | null; date: string | null }) => [m.body, day(m.date)].filter(Boolean).join(", ");
export const quoteBlock = (q: QuoteSrc, where: string) => `> "${q.text.replace(/"/g, "'")}"\n> — ${q.speaker}, ${where}`;
export const clipBlock = (c: { meetingId: string; start: number; end: number; caption: string }) =>
  `[[clip ${c.meetingId} ${clock(c.start)}-${clock(c.end)}${c.caption ? ` "${c.caption.replace(/"/g, "'")}"` : ""}]]`;

/** Words as the site counts them: clip, vote, chart and image lines are not words. */
const plainWords = (md: string) => md.replace(/^\[\[(clip|vote|chart)[^\n]*\]\]$/gm, "").replace(/!\[[^\]]*\]\([^)]*\)/g, "").split(/\s+/).filter(Boolean).length;

function useNeighborhoods() {
  const [list, setList] = useState<Neighborhood[]>([]);
  useEffect(() => { call<{ neighborhoods: Neighborhood[] }>(`${API}/neighborhoods`).then((d) => d && setList(d.neighborhoods)); }, []);
  return list;
}

function Head({ title, children }: { title: string; children?: ReactNode }) {
  return <header className="access-head"><h1 className="page-title">{title}</h1>{children && <div className="art-head__actions">{children}</div>}</header>;
}

// ---------------- Home ----------------

export function AdminArticles() {
  useEffect(() => { document.title = "Articles · Chicago Politics"; }, []);
  const [articles, setArticles] = useState<ArticleSummary[] | null>(null);
  const [sugg, setSugg] = useState<Suggestion[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [planFor, setPlanFor] = useState<string | null>(null);
  const nbs = useNeighborhoods();
  const nbName = useMemo(() => new Map(nbs.map((n) => [n.slug, n.name])), [nbs]);
  useEffect(() => {
    call<{ articles: ArticleSummary[] }>(API).then((d) => setArticles(d?.articles ?? []));
    call<{ suggestions: Suggestion[] }>(`${API}/suggestions`).then((d) => setSugg(d?.suggestions ?? []));
  }, []);
  const open = (a: { id: string }) => { location.href = `/dashboard/admin/articles/${a.id}`; };
  const blank = async (s?: Suggestion) => {
    setBusy(s ? `blank:${s.record}` : "new");
    const a = await call<Article>(API, "POST", s
      ? { title: s.title, records: [s.record], wards: s.wards, neighborhoods: s.neighborhoods.map((n) => n.slug) }
      : { title: "Untitled" });
    setBusy(null);
    if (a) open(a);
  };
  const aiDraft = async (s: Suggestion) => {
    setBusy(`ai:${s.record}`);
    const d = await call<Partial<Article>>(`${API}/ai`, "POST", { action: "draft", record: s.record });
    const a = d ? await call<Article>(API, "POST", d) : null;
    setBusy(null);
    if (a) open(a);
  };
  return (
    <div className="access-page admin-page art-page">
      <Head title="Articles">
        <a className="btn btn--secondary" href="/on-my-block" target="_blank" rel="noopener"><ExternalLink size={16} aria-hidden />On My Block</a>
        <button className="btn btn--primary" disabled={busy === "new"} onClick={() => blank()}><FileText size={16} aria-hidden />New article</button>
      </Head>
      <section className="card art-card">
        <h2 className="art-card__title">Suggested</h2>
        {sugg == null ? <p className="muted">Loading…</p> : !sugg.length ? <p className="muted">Every recent ordinance has an article.</p> : (
          <List variant="plain" className="art-sugg">
            {sugg.slice(0, 12).map((s) => (
              <ListRow key={s.record} className="art-sugg__row">
                <RowMain>
                  <RowTitle><a href={`/dashboard/legislation/${encodeURIComponent(s.matterId)}`}>{s.title}</a></RowTitle>
                  <RowSub>{[s.record, `Passed ${day(s.actionDate)}`, s.sponsor ? `${s.sponsor.name}${s.sponsor.ward ? ` (${s.sponsor.ward})` : ""}` : null, s.divided ? "Divided vote" : null].filter(Boolean).join(" · ")}</RowSub>
                  {s.neighborhoods.length > 0 && <span className="art-chips">{s.neighborhoods.map((n) => <span key={n.slug} className="chip chip--neutral">{n.name}</span>)}</span>}
                </RowMain>
                <RowCell align="end">
                  <span className="art-row-actions">
                    <button className="btn btn--secondary btn--small" aria-expanded={planFor === s.record} onClick={() => setPlanFor(planFor === s.record ? null : s.record)}>Plan</button>
                    <button className="btn btn--secondary btn--small" disabled={!!busy} onClick={() => blank(s)}>Blank</button>
                    <button className="btn btn--primary btn--small" disabled={!!busy} onClick={() => aiDraft(s)}>
                      <Sparkles size={14} aria-hidden />{busy === `ai:${s.record}` ? "Drafting…" : "Draft with AI"}</button>
                  </span>
                </RowCell>
              </ListRow>
            ))}
          </List>
        )}
        {planFor && <TestPlan record={planFor} />}
      </section>
      <section className="card art-card">
        <h2 className="art-card__title">Articles</h2>
        {articles == null ? <p className="muted">Loading…</p> : !articles.length ? <p className="muted">No articles yet.</p> : (
          <List variant="plain">
            {articles.map((a) => (
              <ListRow key={a.id} href={`/dashboard/admin/articles/${a.id}`}>
                <RowMain>
                  <RowTitle>{a.title}</RowTitle>
                  <RowSub>{[a.neighborhoods.map((s) => nbName.get(s) ?? s).join(", "), `${a.readMinutes} min read`, `Edited ${day(a.updatedAt)}`].filter(Boolean).join(" · ")}</RowSub>
                  {!!a.updates?.length && <span className="art-small art-warn">{a.updates.map((u) => `${u.record}: ${u.action} ${day(u.date)}`).join(" · ")}</span>}
                </RowMain>
                <RowCell align="end"><span className="art-row-actions">
                  {a.views28d != null && <span className="art-views" title="Views in the last 28 days (Google Analytics)">{a.views28d.toLocaleString("en-US")} views</span>}
                  {!!a.updates?.length && <span className="chip chip--warning">Record changed</span>}
                  <span className={`chip ${a.status === "published" ? "chip--positive" : "chip--neutral"}`}>{a.status === "published" ? "Published" : "Draft"}</span>
                </span></RowCell>
              </ListRow>
            ))}
          </List>
        )}
      </section>
      <ReaderComments />
      <CaseStudy />
    </div>
  );
}

interface ModComment {
  id: string; city: string; article: string; parentId: string | null; name: string | null; body: string; quote: string | null;
  status: "visible" | "held" | "hidden" | "deleted" | "removed"; reports: number; createdAt: string; reviewedAt: string | null; url: string;
}
const COMMENT_STATUS: Record<ModComment["status"], [string, string]> = {
  visible: ["Showing", "chip--positive"], held: ["Held: reported", "chip--warning"], hidden: ["Hidden", "chip--neutral"], deleted: ["Deleted by author", "chip--neutral"], removed: ["Removed", "chip--neutral"],
};

/** Reader comments on every site's articles (worker/src/block-comments.js): reported or held ones to review; hide, restore or remove. */
function ReaderComments() {
  const [filter, setFilter] = useState<"review" | "hidden" | "all">("review");
  const [city, setCity] = useState("");
  const [r, setR] = useState<{ comments: ModComment[]; sites: { slug: string; siteName: string }[] } | null>(null);
  const load = async () => setR(await call(`${API}/comments?filter=${filter}${city ? `&city=${encodeURIComponent(city)}` : ""}`));
  useEffect(() => { setR(null); load(); }, [filter, city]);
  const act = async (c: ModComment, action: "hide" | "restore" | "remove") => {
    if (action === "remove" && !confirm("Remove this comment? Its text is deleted.")) return;
    if (await call(`${API}/comments/${encodeURIComponent(c.id)}`, "POST", { action })) load();
  };
  const site = (slug: string) => r?.sites.find((x) => x.slug === slug)?.siteName ?? slug;
  return (
    <section className="card art-card">
      <h2 className="art-card__title">Reader comments</h2>
      <p className="muted art-small">Comments readers reported, on every site's articles. Three reports hold a comment until you restore, hide or remove it.</p>
      <div className="art-row-actions">
        {(["review", "hidden", "all"] as const).map((f) => (
          <button key={f} className={`btn btn--small ${filter === f ? "btn--primary" : "btn--secondary"}`} aria-pressed={filter === f} onClick={() => setFilter(f)}>
            {f === "review" ? "To review" : f === "hidden" ? "Hidden" : "Latest"}</button>
        ))}
        <select aria-label="Site" value={city} onChange={(e) => setCity(e.target.value)}>
          <option value="">Every site</option>
          {r?.sites.map((x) => <option key={x.slug} value={x.slug}>{x.siteName}</option>)}
        </select>
      </div>
      {r == null ? <p className="muted">Loading…</p> : !r.comments.length ? <p className="muted">{filter === "review" ? "Nothing to review." : "No comments."}</p> : (
        <List variant="plain">
          {r.comments.map((c) => (
            <ListRow key={c.id}>
              <RowMain>
                <RowTitle>{c.body || <i>(text deleted)</i>}</RowTitle>
                {c.quote && <span className="art-small muted">Quoting: “{c.quote}”</span>}
                <RowSub>{[c.name ?? "Reader", site(c.city), c.article, day(c.createdAt), c.reports ? `${c.reports} report${c.reports === 1 ? "" : "s"}` : null, c.parentId ? "Reply" : null].filter(Boolean).join(" · ")}</RowSub>
              </RowMain>
              <RowCell align="end"><span className="art-row-actions">
                <span className={`chip ${COMMENT_STATUS[c.status][1]}`}>{COMMENT_STATUS[c.status][0]}</span>
                <a className="btn btn--secondary btn--small" href={c.url} target="_blank" rel="noopener"><ExternalLink size={14} aria-hidden />View</a>
                {(c.status === "held" || c.status === "hidden") && <button className="btn btn--secondary btn--small" onClick={() => act(c, "restore")}>Restore</button>}
                {c.status === "visible" && c.reports > 0 && !c.reviewedAt && <button className="btn btn--secondary btn--small" title="Reviewed: leave it up" onClick={() => act(c, "restore")}>Keep</button>}
                {(c.status === "visible" || c.status === "held") && <button className="btn btn--secondary btn--small" onClick={() => act(c, "hide")}>Hide</button>}
                {c.status !== "deleted" && c.status !== "removed" && <button className="btn btn--secondary btn--small" onClick={() => act(c, "remove")}><Trash2 size={14} aria-hidden />Remove</button>}
              </span></RowCell>
            </ListRow>
          ))}
        </List>
      )}
    </section>
  );
}

// ---------------- Editor ----------------

type Draft = Pick<Article, "title" | "dek" | "body" | "slug" | "author" | "neighborhoods" | "wards" | "records" | "hero">;
const draftOf = (a: Article): Draft => ({ title: a.title, dek: a.dek, body: a.body, slug: a.slug, author: a.author, neighborhoods: a.neighborhoods, wards: a.wards, records: a.records, hero: a.hero });

export function AdminArticle({ id }: { id: string }) {
  const [saved, setSaved] = useState<Article | null>(null);
  const [d, setD] = useState<Draft | null>(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [headlines, setHeadlines] = useState<{ title: string; dek: string }[] | null>(null);
  const editorRef = useRef<EditorHandle>(null);
  const [video, setVideo] = useState<VideoPick | null>(null);
  /** The chart picker: a new chart (apply inserts it) or one already in the text (apply replaces its spec). */
  const [chart, setChart] = useState<{ initial?: ChartSpec; apply: (md: string) => void } | null>(null);
  const [mdView, setMdView] = useState(false);
  const [mdPushed, setMdPushed] = useState(false);   // the Markdown view was used: its text replaces the shared document
  const collab = useCollab(id);
  const nbs = useNeighborhoods();

  useEffect(() => {
    fetch(`${API}/${encodeURIComponent(id)}`, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)).catch(() => null)
      .then((a: Article | null) => { if (!a) { setMissing(true); return; } setSaved(a); setD(draftOf(a)); document.title = `${a.title} · Articles · Chicago Politics`; });
  }, [id]);
  const dirty = !!(saved && d && JSON.stringify(draftOf(saved)) !== JSON.stringify(d));
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => { if (dirty) e.preventDefault(); };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const save = async (extra: Partial<Article> = {}) => {
    if (!d) return null;
    setBusy("save");
    const a = await call<Article>(`${API}/${encodeURIComponent(id)}`, "PATCH", { ...d, ...extra });
    setBusy(null);
    if (a) { setSaved(a); setD(draftOf(a)); toastStore.getState().show({ message: extra.status === "published" ? "Published." : extra.status === "draft" ? "Moved to drafts." : "Saved." }); }
    return a;
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") { e.preventDefault(); save(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (missing) return <div className="access-page admin-page"><Head title="Articles" /><p className="muted">This article doesn't exist.</p></div>;
  if (!saved || !d) return <div className="access-page admin-page"><Head title="Articles" /><p className="muted">Loading…</p></div>;

  const set = (patch: Partial<Draft>) => setD({ ...d, ...patch });
  /** Insert a block at the cursor (the editor puts it on its own lines). A vote widget's record joins the article's records. */
  const insert = (block: string) => {
    editorRef.current?.insertMarkdown(block);
    const recs = [...block.matchAll(/\[\[vote\s+([A-Za-z]+\d{4}-\d+)\s*\]\]/g)].map((m) => m[1]).filter((r) => !d.records.includes(r));
    if (recs.length) setD((cur) => (cur ? { ...cur, records: [...cur.records, ...recs] } : cur));
  };
  const rewrite = async (action: "improve" | "shorten") => {
    const selected = editorRef.current?.selectionText() ?? "";
    const text = selected.trim() || d.body;
    if (!text.trim()) return;
    setBusy(action);
    const out = await call<{ text: string }>(`${API}/ai`, "POST", { action, text });
    setBusy(null);
    if (out?.text) { if (selected.trim()) editorRef.current?.replaceSelection(out.text); else set({ body: out.text }); }
  };
  /** A block as the public page draws it (clips, the vote widget, charts): the editor's node views ask for it. */
  const render = async (md: string) => (await call<{ html: string }>(`${API}/render`, "POST", { body: md }))?.html ?? "";
  /** The video popup for a meeting at a moment (from a clip in the text, which it can then update, or from Sources). */
  const openVideo = async (meetingId: string, start: number, existing?: ExistingClip) => {
    const p = await call<{ vimeo: string | null; body: string | null; date: string | null }>(`${API}/passages?meeting=${encodeURIComponent(meetingId)}&t=${Math.floor(start)}&window=1`);
    if (!p?.vimeo) { toastStore.getState().show({ message: "No video for that meeting yet." }); return; }
    setVideo({ meetingId, vimeo: p.vimeo, start, label: meetingLabel({ body: p.body, date: p.date }) || meetingId, existing });
  };
  /** A chart in the text: open the picker on its spec; the edited spec replaces it. */
  const editChart = (spec: string, apply: (spec: string) => void) =>
    setChart({ initial: parseChartSpec(spec) ?? undefined, apply: (md) => apply(md.replace(/^\[\[chart\s+/, "").replace(/\s*\]\]$/, "")) });
  const suggestHeadlines = async () => {
    setBusy("headline");
    const out = await call<{ headlines: { title: string; dek: string }[] }>(`${API}/ai`, "POST", { action: "headline", text: `${d.title}\n\n${d.body}` });
    setBusy(null);
    if (out) setHeadlines(out.headlines);
  };
  const showPreview = async () => {
    if (preview != null) { setPreview(null); return; }
    const a = dirty ? await save() : saved;
    if (!a) return;
    const p = await call<{ html: string }>(`${API}/${encodeURIComponent(id)}/preview`);
    if (p) setPreview(p.html);
  };
  const upload = async (file: Blob) => {
    setBusy("upload");
    const out = await call<{ url: string }>(`${API}/upload`, "POST", undefined, file);
    setBusy(null);
    return out?.url ?? null;
  };
  const published = saved.status === "published";
  // Counted as the site counts them (worker/src/block.js plainWords): clip, vote, chart and image lines are not words.
  const words = plainWords(d.body);

  return (
    <div className="access-page admin-page art-page">
      <Head title={published ? "Published article" : "Draft"}>
        <a className="btn btn--secondary" href="/dashboard/admin/articles">All articles</a>
        {published && <a className="btn btn--secondary" href={`/on-my-block/${saved.slug}`} target="_blank" rel="noopener"><ExternalLink size={16} aria-hidden />View</a>}
        <button className="btn btn--secondary" disabled={!dirty || !!busy} onClick={() => save()}>{busy === "save" ? "Saving…" : dirty ? "Save" : "Saved"}</button>
        <button className="btn btn--primary" disabled={!!busy} onClick={() => save({ status: published ? "draft" : "published" })}>{published ? "Unpublish" : "Publish"}</button>
      </Head>
      <div className="art-editor">
        <div className="art-main card">
          <input className="art-title" value={d.title} onChange={(e) => set({ title: e.target.value })} aria-label="Title" placeholder="Title" />
          <textarea className="art-dek" rows={2} value={d.dek} onChange={(e) => set({ dek: e.target.value })} aria-label="Dek" placeholder="One sentence under the title" />
          <div className="art-toolbar" role="toolbar" aria-label="Writing help">
            <button className="btn btn--secondary btn--small" disabled={!!busy} onClick={() => rewrite("improve")}><Sparkles size={14} aria-hidden />{busy === "improve" ? "Editing…" : "Improve"}</button>
            <button className="btn btn--secondary btn--small" disabled={!!busy} onClick={() => rewrite("shorten")}>{busy === "shorten" ? "Shortening…" : "Shorten"}</button>
            <button className="btn btn--secondary btn--small" disabled={!!busy} onClick={suggestHeadlines}>{busy === "headline" ? "Thinking…" : "Headlines"}</button>
            <label className="btn btn--secondary btn--small art-file">
              <ImagePlus size={14} aria-hidden />Image
              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                const f = e.target.files?.[0]; e.target.value = "";
                const url = f ? await upload(f) : null;
                if (url) insert(`![${f!.name.replace(/\.\w+$/, "")}](${url} "Caption")`);
              }} />
            </label>
            <button className="btn btn--secondary btn--small" onClick={() => setChart({ apply: insert })}><BarChart3 size={14} aria-hidden />Chart</button>
            <span className="art-toolbar__end">
              <span className="muted">{words.toLocaleString()} words · {Math.max(1, Math.round(words / 230))} min</span>
              <button className="btn btn--secondary btn--small" aria-pressed={mdView} disabled={preview != null} onClick={() => { setMdPushed(mdView); setMdView(!mdView); }}>{mdView ? "Rich text" : "Markdown"}</button>
              <button className="btn btn--secondary btn--small" aria-pressed={preview != null} onClick={showPreview}>{preview != null ? "Edit" : "Preview"}</button>
            </span>
          </div>
          {headlines && (
            <div className="art-headlines">
              {headlines.map((h, i) => (
                <button key={i} className="art-headline" onClick={() => { set({ title: h.title, dek: h.dek }); setHeadlines(null); }}>
                  <b>{h.title}</b><span>{h.dek}</span>
                </button>
              ))}
              <button className="link-button" onClick={() => setHeadlines(null)}>Close</button>
            </div>
          )}
          {collab && (
            <p className="art-presence" role="status">
              <span className={`art-presence__dot is-${collab.status}`} aria-hidden />
              {collab.status === "live" ? "Live" : collab.status === "connecting" ? "Connecting…" : "Reconnecting…"}
              {collab.peers.length ? <> · Editing with {collab.peers.map((p) => <span key={p.id + p.name} className="art-presence__peer" style={{ "--peer": p.color } as React.CSSProperties}>{p.name}</span>)}</> : " · Only you"}
            </p>
          )}
          {preview != null
            ? <div className="art-preview block-body" dangerouslySetInnerHTML={{ __html: preview }} />
            : mdView
              ? <textarea className="text-input art-md" value={d.body} onChange={(e) => set({ body: e.target.value })} aria-label="Markdown" spellCheck={false} />
              : collab === null || (collab && !collab.synced)
                ? <p className="muted art-small">Opening the shared draft…</p>
                : <ArticleEditor ref={editorRef} value={d.body} onChange={(body) => set({ body })} upload={upload} render={render} openVideo={openVideo} editChart={editChart}
                    collab={collab ? { doc: collab.doc, provider: collab.provider, user: collab.user } : undefined} pushValueOnCreate={mdPushed} />}
        </div>
        <aside className="art-side">
          <section className="card art-card">
            <h2 className="art-card__title">Publish</h2>
            <label className="field-label" htmlFor="art-slug">URL</label>
            <div className="art-slug"><span className="muted">/on-my-block/</span><input id="art-slug" className="text-input" value={d.slug} onChange={(e) => set({ slug: e.target.value })} /></div>
            <label className="field-label" htmlFor="art-author">Byline</label>
            <input id="art-author" className="text-input" value={d.author} onChange={(e) => set({ author: e.target.value })} />
            <p className="muted art-small">{published ? `Published ${day(saved.publishedAt)}` : "Draft"} · Edited {day(saved.updatedAt)}</p>
            {!!saved.updates?.length && (
              <div className="art-stale" role="status">
                <b>The record changed after this was last edited.</b>
                {saved.updates.map((u) => <span key={`${u.record}${u.date}${u.action}`}>{u.record}: {u.action}, {day(u.date)}{u.text ? `. ${u.text}` : ""}</span>)}
              </div>
            )}
            <SearchChecks d={d} nbs={nbs} />
            <button className="btn btn--secondary btn--small art-delete" disabled={!!busy} onClick={async () => {
              if (!confirm("Delete this article? This can't be undone.")) return;
              if (await call(`${API}/${encodeURIComponent(id)}`, "DELETE")) location.href = "/dashboard/admin/articles";
            }}><Trash2 size={14} aria-hidden />Delete</button>
          </section>
          <Places d={d} set={set} nbs={nbs} />
          <AutoSources body={d.body} records={d.records} neighborhoods={d.neighborhoods} />
          <section className="card art-card">
            <h2 className="art-card__title">Image</h2>
            {d.hero?.url && <img className="art-hero" src={d.hero.url} alt="" />}
            <label className="btn btn--secondary btn--small art-file">
              <ImagePlus size={14} aria-hidden />{busy === "upload" ? "Uploading…" : d.hero?.url ? "Replace" : "Upload"}
              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                const f = e.target.files?.[0]; e.target.value = "";
                const url = f ? await upload(f) : null;
                if (url) set({ hero: { url, alt: d.hero?.alt ?? "", credit: d.hero?.credit ?? "" } });
              }} />
            </label>
            {d.hero?.url && <>
              <label className="field-label" htmlFor="art-alt">Alt text</label>
              <input id="art-alt" className="text-input" value={d.hero.alt} onChange={(e) => set({ hero: { ...d.hero!, alt: e.target.value } })} />
              <label className="field-label" htmlFor="art-credit">Credit</label>
              <input id="art-credit" className="text-input" value={d.hero.credit} onChange={(e) => set({ hero: { ...d.hero!, credit: e.target.value } })} />
              <button className="link-button" onClick={() => set({ hero: null })}>Remove</button>
            </>}
          </section>
          <SourcesPanel records={d.records} insert={insert} onImage={(url, alt) => set({ hero: d.hero ?? { url, alt, credit: "" } })} onOpenVideo={openVideo} />
        </aside>
      </div>
      {chart && <ChartPicker articleText={`${d.title}\n\n${d.body}`} records={d.records} render={render} initial={chart.initial} onInsert={(md) => { chart.apply(md); setChart(null); }} onClose={() => setChart(null)} />}
      {video && <VideoModal pick={video} onClose={() => setVideo(null)} upload={upload}
        onQuote={(md) => { insert(md); setVideo(null); }} onClip={(md) => { insert(md); setVideo(null); }} onImage={(md) => { insert(md); setVideo(null); }}
        onHero={(url, alt) => { set({ hero: { url, alt, credit: "City of Chicago video" } }); setVideo(null); }} />}
      <DynamicArticle id={id} nbs={nbs} />
      <ReaderTest id={id} original={saved} />
    </div>
  );
}

/** The Sources list the page will show, added automatically and kept current as the draft changes (block.js articleSources). */
function AutoSources({ body, records, neighborhoods }: { body: string; records: string[]; neighborhoods: string[] }) {
  const [list, setList] = useState<{ kind: string; title: string; url: string }[] | null>(null);
  useEffect(() => {
    const t = setTimeout(() => { call<{ sources: { kind: string; title: string; url: string }[] }>(`${API}/sources-preview`, "POST", { body, records, neighborhoods }).then((r) => setList(r?.sources ?? [])); }, 800);
    return () => clearTimeout(t);
  }, [body, records.join(","), neighborhoods.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <section className="card art-card">
      <h2 className="art-card__title">Sources on the page</h2>
      <p className="muted art-small">Added automatically: the Clerk's record and documents for each piece of legislation, the meetings of your clips and votes, outside reporting you link to, then our pages.</p>
      {list == null ? <p className="muted art-small">Loading…</p> : !list.length ? <p className="muted art-small">Add a record, a clip or a link and its source appears here.</p> : (
        <ol className="art-autosrc">{list.map((x) => <li key={x.url}><span className="muted">{x.kind}</span> <a href={x.url} target="_blank" rel="noopener">{x.title}</a></li>)}</ol>
      )}
    </section>
  );
}

// ---------------- Dynamic article (block-dynamic.js) ----------------

type Perspective = "supporters" | "opponents" | "residents" | "balanced";
interface Section { key: string; heading: string; body: string; status: "draft" | "active" | "paused"; checks: { value: string; message: string; reviewed?: boolean }[] }
interface DynamicState {
  dynamic: { enabled: boolean; mode: "test" | "introduce" | "match"; introduce: Perspective[] };
  sections: Section[]; candidates: { key: string; label: string }[];
  readers: { perspective: Perspective; neighborhood: string | null; readers: number; avgSeconds: number; avgScroll: number }[];
}
const MODE_LABEL = {
  test: "Reader test: the test picks each reader's version",
  introduce: "Introduce new perspectives: each reader gets the perspective they've read least, in this order",
  match: "Match the reader: the version written for what they told us fits them",
};

/**
 * The master article (what search engines, and everyone when this is off, get) and what a reader's page adds: their
 * neighborhood's narrative, a critical look at their alderperson from their side when they said how they'd vote, and
 * which version they get. The headline never changes.
 */
function DynamicArticle({ id, nbs }: { id: string; nbs: Neighborhood[] }) {
  const base = `${API}/${encodeURIComponent(id)}/dynamic`;
  const [s, setS] = useState<DynamicState | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<Section | null>(null);
  const load = async () => setS(await call<DynamicState>(base));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (key: string, f: () => Promise<DynamicState | null>) => { setBusy(key); const next = await f(); setBusy(null); if (next) setS(next); };
  if (!s) return null;
  const d = s.dynamic;
  const save = (p: Partial<DynamicState["dynamic"]>) => act("settings", () => call<DynamicState>(base, "PATCH", p));
  const byKey = new Map(s.sections.map((x) => [x.key, x]));
  const nbName = (slug: string | null) => (slug ? nbs.find((n) => n.slug === slug)?.name ?? slug : "No neighborhood section");
  const move = (p: Perspective, by: number) => {
    const list = [...d.introduce]; const i = list.indexOf(p); const j = i + by;
    if (i < 0 || j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]]; save({ introduce: list });
  };
  return (
    <section className="card art-card art-test">
      <div className="art-test__head">
        <h2 className="art-card__title">Dynamic article</h2>
        <label className="art-row-actions"><input type="checkbox" checked={d.enabled} disabled={!!busy} onChange={(e) => save({ enabled: e.target.checked })} />
          {d.enabled ? "On: readers get sections written for them" : "Off: everyone reads the master article"}</label>
      </div>
      <p className="muted art-small">The article above is the master. Search engines always get it, and so does everyone while this is off. Readers keep its headline whatever they get.</p>
      <h3 className="art-card__title">Steering</h3>
      {(Object.keys(MODE_LABEL) as (keyof typeof MODE_LABEL)[]).map((m) => (
        <label key={m} className="art-small art-radio"><input type="radio" name={`mode-${id}`} checked={d.mode === m} disabled={!!busy} onChange={() => save({ mode: m })} /> {MODE_LABEL[m]}</label>
      ))}
      {d.mode === "introduce" && (
        <ol className="art-order">
          {d.introduce.map((p, i) => (
            <li key={p}>{PERSPECTIVE_LABEL[p]}
              <button className="link-button" disabled={i === 0} onClick={() => move(p, -1)} aria-label={`Move ${p} up`}>↑</button>
              <button className="link-button" disabled={i === d.introduce.length - 1} onClick={() => move(p, 1)} aria-label={`Move ${p} down`}>↓</button>
            </li>
          ))}
        </ol>
      )}
      <h3 className="art-card__title">Sections for readers</h3>
      <List variant="plain">
        {s.candidates.map((c) => {
          const x = byKey.get(c.key);
          return (
            <ListRow key={c.key}>
              <RowMain>
                <RowTitle>{x?.heading ?? c.label}</RowTitle>
                <RowSub>{c.label}</RowSub>
                {x?.checks.filter((k) => !k.reviewed).map((k) => <span key={k.value} className="art-small art-warn">{k.message}</span>)}
              </RowMain>
              <RowCell align="end"><span className="art-row-actions">
                {x && <span className={`chip ${x.status === "active" ? "chip--positive" : "chip--neutral"}`}>{x.status === "active" ? "Live" : x.status === "paused" ? "Paused" : "Draft"}</span>}
                <button className="btn btn--secondary btn--small" disabled={!!busy} onClick={() => act(c.key, () => call<DynamicState>(`${base}/sections/generate`, "POST", { key: c.key }))}>
                  <Sparkles size={14} aria-hidden />{busy === c.key ? "Writing…" : x ? "Rewrite" : "Write with AI"}</button>
                {x && <button className="btn btn--secondary btn--small" onClick={() => setEditing(x)}>Edit</button>}
                {x && <button className="btn btn--secondary btn--small" disabled={!!busy} onClick={() => act(c.key, () => call<DynamicState>(`${base}/sections/${encodeURIComponent(c.key)}`, "PATCH", { status: x.status === "active" ? "paused" : "active" }))}>{x.status === "active" ? "Pause" : "Go live"}</button>}
              </span></RowCell>
            </ListRow>
          );
        })}
      </List>
      {editing && (
        <div className="art-variant-edit">
          <input className="text-input" value={editing.heading} onChange={(e) => setEditing({ ...editing, heading: e.target.value })} aria-label="Heading" />
          <textarea className="text-input art-variant-body" value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} aria-label="Section" />
          <span className="art-row-actions">
            <button className="btn btn--primary btn--small" disabled={!!busy} onClick={() => act("save", async () => {
              const next = await call<DynamicState>(`${base}/sections/${encodeURIComponent(editing.key)}`, "PATCH", { heading: editing.heading, body: editing.body });
              if (next) setEditing(null);
              return next;
            })}>Save</button>
            <button className="btn btn--secondary btn--small" onClick={() => setEditing(null)}>Cancel</button>
          </span>
        </div>
      )}
      {s.readers.length > 0 && (
        <div className="art-table-wrap"><table className="art-table">
          <thead><tr><th>Perspective read</th><th>Neighborhood section</th><th>Readers</th><th>Avg. time</th><th>Avg. scroll</th></tr></thead>
          <tbody>{s.readers.map((r) => (
            <tr key={`${r.perspective}|${r.neighborhood}`}><td>{PERSPECTIVE_LABEL[r.perspective]}</td><td>{nbName(r.neighborhood)}</td><td>{r.readers.toLocaleString()}</td><td>{r.avgSeconds}s</td><td>{pct(r.avgScroll)}</td></tr>
          ))}</tbody>
        </table></div>
      )}
    </section>
  );
}

// ---------------- Case study (block-study.js) ----------------

interface Participant {
  userId: string; email: string; name: string | null; cohort: string; consentedAt: string; enrolledAt: string; notes: string; withdrawnAt: string | null;
  estimate: {
    lean: { score: number | null; provisional: number | null; positions: number; evidence: { record: string; title: string; position: string; sideScore: number }[]; unavailable?: boolean };
    reading: { perspective: Perspective; articles: number; avgSeconds: number; avgScroll: number }[]; neighborhood: string | null; roles: string[];
  } | null;
}

const leanText = (x: number) => `${x > 0 ? "+" : ""}${x.toFixed(2)} (${x <= -0.33 ? "progressive side" : x >= 0.33 ? "conservative side" : "middle"})`;

/** The study's participants (consent on file), each with their estimate; withdraw them here. */
function CaseStudy() {
  const [r, setR] = useState<{ participants: Participant[]; method: string; minPositions: number } | null>(null);
  const load = async () => setR(await call(`${API}/study`));
  useEffect(() => { load(); }, []);
  const nbs = useNeighborhoods();
  return (
    <section className="card art-card art-test">
      <h2 className="art-card__title">Case study</h2>
      <p className="muted art-small">Participants whose consent is on file. Withdrawing stops the estimate.</p>
      {r == null ? <p className="muted">Loading…</p> : !r.participants.length ? <p className="muted">No participants yet.</p> : (
        <List variant="plain">
          {r.participants.map((p) => (
            <ListRow key={p.userId}>
              <RowMain>
                <RowTitle>{p.name || p.email}</RowTitle>
                <RowSub>{[p.email, p.cohort, `Consent ${day(p.consentedAt)}`, p.withdrawnAt ? `Withdrawn ${day(p.withdrawnAt)}` : null, p.estimate?.neighborhood ? nbs.find((n) => n.slug === p.estimate!.neighborhood)?.name : null].filter(Boolean).join(" · ")}</RowSub>
                {p.estimate && <dl className="art-dir">
                  <dt>Lean</dt><dd>{p.estimate.lean.unavailable ? "The Council's vote axis isn't available" : p.estimate.lean.score != null ? `${leanText(p.estimate.lean.score)}, from ${p.estimate.lean.positions} positions`
                    : p.estimate.lean.provisional != null ? `Too few positions to say (${p.estimate.lean.positions} of ${r.minPositions}); so far ${leanText(p.estimate.lean.provisional)}` : "No positions on divided votes yet"}</dd>
                  <dt>Read</dt><dd>{p.estimate.reading.filter((x) => x.articles).map((x) => `${PERSPECTIVE_LABEL[x.perspective]}: ${x.articles} (${x.avgSeconds}s, ${pct(x.avgScroll)})`).join(" · ") || "Nothing yet"}</dd>
                  {p.estimate.roles.length > 0 && <><dt>Said</dt><dd>{p.estimate.roles.join(", ")}</dd></>}
                </dl>}
              </RowMain>
              <RowCell align="end">{!p.withdrawnAt && <button className="btn btn--secondary btn--small" onClick={async () => { if (confirm(`Withdraw ${p.email} from the study?`) && await call(`${API}/study/${encodeURIComponent(p.userId)}`, "DELETE")) load(); }}>Withdraw</button>}</RowCell>
            </ListRow>
          ))}
        </List>
      )}
      {r && <p className="muted art-small">{r.method}</p>}
    </section>
  );
}

function Places({ d, set, nbs }: { d: Draft; set: (p: Partial<Draft>) => void; nbs: Neighborhood[] }) {
  const [q, setQ] = useState("");
  const matches = q.trim() ? nbs.filter((n) => !d.neighborhoods.includes(n.slug) && [n.name, ...n.alsoKnownAs].some((x) => x.toLowerCase().includes(q.trim().toLowerCase()))).slice(0, 6) : [];
  const name = (s: string) => nbs.find((n) => n.slug === s)?.name ?? s;
  const addNb = (n: Neighborhood) => { set({ neighborhoods: [...d.neighborhoods, n.slug], wards: [...new Set([...d.wards, ...n.wards])].sort((a, b) => a - b) }); setQ(""); };
  return (
    <section className="card art-card">
      <h2 className="art-card__title">Places</h2>
      <label className="field-label" htmlFor="art-nb">Neighborhoods</label>
      <span className="art-chips">{d.neighborhoods.map((s) => (
        <button key={s} className="chip chip--neutral art-chip" onClick={() => set({ neighborhoods: d.neighborhoods.filter((x) => x !== s) })} aria-label={`Remove ${name(s)}`}>{name(s)} ×</button>
      ))}</span>
      <input id="art-nb" className="text-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Add a neighborhood"
        onKeyDown={(e) => { if (e.key === "Enter" && matches[0]) { e.preventDefault(); addNb(matches[0]); } }} />
      {matches.length > 0 && <div className="art-options">{matches.map((n) => <button key={n.slug} className="art-option" onClick={() => addNb(n)}>{n.name}<span className="muted"> · Wards {n.wards.join(", ")}</span></button>)}</div>}
      <label className="field-label" htmlFor="art-wards">Wards</label>
      <input id="art-wards" className="text-input" value={d.wards.join(", ")} onChange={(e) => set({ wards: e.target.value.split(/[,\s]+/).map(Number).filter((w) => w >= 1 && w <= 50) })} />
      <label className="field-label" htmlFor="art-records">Legislation</label>
      <input id="art-records" className="text-input" value={d.records.join(", ")} placeholder="O2026-0026270"
        onChange={(e) => set({ records: e.target.value.split(/[,\s]+/).map((r) => r.trim().toUpperCase()).filter(Boolean) })} />
    </section>
  );
}

/** Something the editor can take by drag and drop: its Markdown rides along as text/x-omb-block. */
const drag = (md: string) => ({ draggable: true, onDragStart: (e: React.DragEvent) => { e.dataTransfer.setData(OMB_DRAG, md); e.dataTransfer.setData("text/plain", md); e.dataTransfer.effectAllowed = "copy"; } });

function SourcesPanel({ records, insert, onImage, onOpenVideo }: { records: string[]; insert: (block: string) => void; onImage: (url: string, alt: string) => void; onOpenVideo: (meetingId: string, t: number) => void }) {
  const [record, setRecord] = useState(records[0] ?? "");
  const [src, setSrc] = useState<Sources | null>(null);
  const [extra, setExtra] = useState<MeetingSrc | null>(null);
  const [meeting, setMeeting] = useState(""); const [ward, setWard] = useState("");
  const [loading, setLoading] = useState(false);
  const load = async (r = record) => {
    if (!r.trim()) return;
    setLoading(true);
    setSrc(await call<Sources>(`${API}/sources?record=${encodeURIComponent(r.trim())}`));
    setLoading(false);
  };
  useEffect(() => { if (records[0]) load(records[0]); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const loadAlder = async () => {
    if (!meeting.trim()) return;
    const id = meeting.trim().match(/[0-9A-F]{8}-[0-9A-F-]{27}/i)?.[0] ?? meeting.trim();
    setLoading(true);
    setExtra(await call<MeetingSrc>(`${API}/sources?meeting=${encodeURIComponent(id)}${ward ? `&ward=${encodeURIComponent(ward)}` : ""}`));
    setLoading(false);
  };
  const meetings = [...(src?.meetings ?? []), ...(extra ? [extra] : [])];
  return (
    <section className="card art-card art-sources">
      <h2 className="art-card__title">Sources</h2>
      <form className="art-inline" onSubmit={(e) => { e.preventDefault(); load(); }}>
        <input className="text-input" value={record} onChange={(e) => setRecord(e.target.value)} placeholder="Record, e.g. O2026-0026270" aria-label="Record" />
        <button className="btn btn--secondary btn--small" disabled={loading}>Find</button>
      </form>
      <form className="art-inline" onSubmit={(e) => { e.preventDefault(); loadAlder(); }}>
        <input className="text-input" value={meeting} onChange={(e) => setMeeting(e.target.value)} placeholder="Meeting link or id" aria-label="Meeting" />
        <input className="text-input art-ward" value={ward} onChange={(e) => setWard(e.target.value)} placeholder="Ward" aria-label="Ward" inputMode="numeric" />
        <button className="btn btn--secondary btn--small" disabled={loading}>Find</button>
      </form>
      {loading && <p className="muted">Loading…</p>}
      {src && (
        <div className="art-src">
          <p><a href={`/dashboard/legislation/${encodeURIComponent(src.matter.id)}`} target="_blank" rel="noopener"><b>{src.matter.nickname || src.matter.title}</b></a></p>
          <p className="muted art-small">{[src.matter.record, src.matter.action && `${src.matter.action} ${day(src.matter.actionDate)}`, src.matter.sponsor].filter(Boolean).join(" · ")}</p>
          {src.summary?.summary && <p className="art-small">{src.summary.summary}</p>}
          {src.matter.action && /Passed|Adopted|Failed|Approved/.test(src.matter.action) && (
            <div className="art-src__item" {...drag(`[[vote ${src.matter.record}]]`)}>
              <span>How each ward voted on {src.matter.record}</span>
              <button className="btn btn--secondary btn--small" onClick={() => insert(`[[vote ${src.matter.record}]]`)}>Insert</button>
            </div>
          )}
        </div>
      )}
      {meetings.map((m) => (
        <div key={m.meetingId} className="art-src">
          <p className="art-src__meeting">{m.thumbnail && <button className="art-thumb" title="Use as the article's image" onClick={() => onImage(m.thumbnail!, meetingLabel(m))}><img src={m.thumbnail} alt="" loading="lazy" /></button>}<b>{meetingLabel(m) || m.meetingId}</b>
            {m.vimeo && <button className="link-button" onClick={() => onOpenVideo(m.meetingId, m.speakers[0]?.moments[0]?.t ?? 0)}><Video size={14} aria-hidden /> Open video</button>}</p>
          {m.speakers.slice(0, 4).map((s) => s.moments.slice(0, 2).map((mo) => <p key={`${s.ward}-${mo.t}`} className="muted art-small">{s.name} ({s.ward}) at {clock(mo.t)}: {mo.summary}</p>))}
          {m.clips.slice(0, 6).map((c) => (
            <div key={`c${c.speakerId}${c.start}`} className="art-src__item" {...drag(clipBlock(c))}>
              <Video size={14} aria-hidden />
              <span>{c.speaker} · {clock(c.start)}–{clock(c.end)} <button className="link-button" onClick={() => onOpenVideo(c.meetingId, c.start)}>Open video</button></span>
              <button className="btn btn--secondary btn--small" onClick={() => insert(clipBlock(c))}>Insert</button>
            </div>
          ))}
          {m.quotes.slice(0, 12).map((q, i) => (
            <div key={`q${i}${q.t}`} className="art-src__item" {...drag(quoteBlock(q, meetingLabel(m)))}>
              <Quote size={14} aria-hidden />
              <span><span className="art-quote">"{q.text}"</span> <span className="muted">{q.speaker} · {clock(q.t)}</span></span>
              <button className="btn btn--secondary btn--small" onClick={() => insert(quoteBlock(q, meetingLabel(m)))}>Insert</button>
            </div>
          ))}
          {!m.quotes.length && !m.clips.length && <p className="muted art-small">No transcript with speakers for this meeting yet.</p>}
        </div>
      ))}
    </section>
  );
}

// ---------------- Reader test (block-variants.js, block-experiments.js) ----------------

interface Variant { id: string; label: string; angle: string; title: string; dek: string; body: string; status: "draft" | "active" | "paused";
  perspective: "supporters" | "opponents" | "residents" | "balanced"; position: string; audience: string[]; rationale: string; featured: string[];
  checks: { kind: string; value: string; message: string; reviewed?: boolean }[]; readMinutes: number }
interface ArmResult { arm: string; impressions: number; successes: number; rate: number; low: number; high: number; probBest: number;
  engaged: number; reads: number; ad_views: number; conversions: number; avgDwellSec: number }
interface TestResults { winner: string | null; variants: Variant[]; overall: ArmResult[]; byNeighborhood: { context: string; name: string; arms: ArmResult[] }[] }

const pct = (x: number) => `${Math.round(x * 1000) / 10}%`;
const PERSPECTIVE_LABEL = { supporters: "Supporters' case", opponents: "Opponents' case", residents: "What residents said", balanced: "Both sides" } as const;

interface Plan {
  record: string; title: string; rules: string;
  positions: { support: { who: string; said: string }[]; oppose: { who: string; said: string }[]; other: { who: string; said: string }[] };
  publicComment: { support: string[]; oppose: string[]; total: number };
  neighborhoods: { name: string; wards: number[]; medianIncome?: number; medianAge?: number; renterShare?: number; povertyRate?: number; audiences: string[] }[];
  directions: { perspective: keyof typeof PERSPECTIVE_LABEL; label: string; audience: string[] }[];
}

/** A suggested article's test plan, from the record: the sides, public comment, the neighborhoods and their readers. */
function TestPlan({ record }: { record: string }) {
  const [p, setP] = useState<Plan | null>(null);
  useEffect(() => { setP(null); call<Plan>(`${API}/suggestions/plan?record=${encodeURIComponent(record)}`).then(setP); }, [record]);
  if (!p) return <p className="muted art-small">Loading…</p>;
  const side = (title: string, rows: { who: string; said: string }[]) => rows.length > 0 && (
    <div><h4 className="art-plan__h">{title}</h4><ul className="art-plan__list">{rows.map((r) => <li key={r.who}><b>{r.who}</b> {r.said}</li>)}</ul></div>);
  return (
    <div className="art-plan">
      <h3 className="art-card__title">Test plan · {p.record}</h3>
      <div className="art-plan__grid">
        {side("Supporters", p.positions.support)}
        {side("Opponents", p.positions.oppose)}
        {(p.publicComment.support.length > 0 || p.publicComment.oppose.length > 0) && (
          <div><h4 className="art-plan__h">Public comment ({p.publicComment.total})</h4><ul className="art-plan__list">
            {p.publicComment.support.map((x, i) => <li key={`s${i}`}><b>For</b> {x}</li>)}
            {p.publicComment.oppose.map((x, i) => <li key={`o${i}`}><b>Against</b> {x}</li>)}
          </ul></div>)}
      </div>
      {p.neighborhoods.length > 0 && (
        <div className="art-table-wrap"><table className="art-table">
          <thead><tr><th>Neighborhood</th><th>Wards</th><th>Renters</th><th>Median income</th><th>Median age</th><th>Poverty</th><th>Written for</th></tr></thead>
          <tbody>{p.neighborhoods.map((n) => (
            <tr key={n.name}><td>{n.name}</td><td>{n.wards.join(", ")}</td><td>{n.renterShare != null ? pct(n.renterShare) : "—"}</td>
              <td>{n.medianIncome ? `$${n.medianIncome.toLocaleString()}` : "—"}</td><td>{n.medianAge ?? "—"}</td><td>{n.povertyRate != null ? pct(n.povertyRate) : "—"}</td><td>{n.audiences.join(", ")}</td></tr>
          ))}</tbody>
        </table></div>)}
      <h4 className="art-plan__h">Directions to test</h4>
      <ul className="art-plan__list">{p.directions.map((d) => <li key={d.perspective}><b>{PERSPECTIVE_LABEL[d.perspective]}</b> {d.label} · for {d.audience.join(", ")}</li>)}</ul>
      <p className="muted art-small">{p.rules}</p>
    </div>
  );
}

/**
 * The article's versions and how readers respond (per neighborhood, the experiment's own numbers). A version runs only
 * when staff make it active; the AI drafts versions but never activates them. Every version keeps the article's facts.
 */
function ReaderTest({ id, original }: { id: string; original: Article }) {
  const base = `${API}/${encodeURIComponent(id)}`;
  const [r, setR] = useState<TestResults | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<Variant | null>(null);
  const [ctx, setCtx] = useState("");
  const load = async () => setR(await call<TestResults>(`${base}/variants`));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (key: string, f: () => Promise<unknown>) => { setBusy(key); await f(); setBusy(null); await load(); };
  if (!r) return null;
  const label = (arm: string) => (arm === "control" ? "A · Master article" : (() => { const v = r.variants.find((x) => x.id === arm); return v ? `${v.label} · ${v.angle || v.title}` : arm; })());
  const rows = ctx ? r.byNeighborhood.find((n) => n.context === ctx)?.arms ?? [] : r.overall;
  return (
    <section className="card art-card art-test">
      <div className="art-test__head">
        <h2 className="art-card__title">Reader test</h2>
        <span className="art-row-actions">
          <button className="btn btn--secondary btn--small" disabled={!!busy} onClick={() => act("new", () => call(`${base}/variants`, "POST", { angle: "", title: original.title, dek: original.dek, body: original.body }))}>New version</button>
          <button className="btn btn--primary btn--small" disabled={!!busy} onClick={() => act("gen", () => call(`${base}/variants/generate`, "POST", {}))}>
            <Sparkles size={14} aria-hidden />{busy === "gen" ? "Writing…" : "Write versions with AI"}</button>
        </span>
      </div>
      {r.winner && <p className="art-small">Every reader gets <b>{label(r.winner)}</b>. <button className="link-button" onClick={() => act("w", () => call(`${base}/winner`, "POST", { variant: null }))}>Keep testing</button></p>}
      <List variant="plain">
        {r.variants.map((v) => (
          <ListRow key={v.id}>
            <RowMain>
              <RowTitle>{v.label} · {v.title}</RowTitle>
              <RowSub>{[v.angle, `${v.readMinutes} min read`].filter(Boolean).join(" · ")}</RowSub>
              <dl className="art-dir">
                <dt>Leads with</dt><dd>{PERSPECTIVE_LABEL[v.perspective]}{v.position ? `: ${v.position}` : ""}</dd>
                <dt>Written for</dt><dd>{v.audience.length ? v.audience.join(", ") : "everyone"}</dd>
                {v.featured.length > 0 && <><dt>Features</dt><dd>{v.featured.join(", ")}</dd></>}
                {v.rationale && <><dt>Why</dt><dd>{v.rationale}</dd></>}
              </dl>
              {v.checks.filter((c) => !c.reviewed).map((c) => <span key={c.value} className="art-small art-warn">{c.message}</span>)}
            </RowMain>
            <RowCell align="end">
              <span className="art-row-actions">
                <span className={`chip ${v.status === "active" ? "chip--positive" : "chip--neutral"}`}>{v.status === "active" ? "Testing" : v.status === "paused" ? "Paused" : "Draft"}</span>
                <button className="btn btn--secondary btn--small" onClick={() => setEditing(v)}>Edit</button>
                <button className="btn btn--secondary btn--small" disabled={!!busy} onClick={() => act(v.id, () => call(`${base}/variants/${v.id}`, "PATCH", { status: v.status === "active" ? "paused" : "active" }))}>{v.status === "active" ? "Pause" : "Test"}</button>
                <button className="btn btn--secondary btn--small" aria-label={`Delete version ${v.label}`} disabled={!!busy} onClick={() => confirm(`Delete version ${v.label}?`) && act(v.id, () => call(`${base}/variants/${v.id}`, "DELETE"))}><Trash2 size={14} aria-hidden /></button>
              </span>
            </RowCell>
          </ListRow>
        ))}
      </List>
      {editing && (
        <div className="art-variant-edit">
          <input className="text-input" value={editing.angle} onChange={(e) => setEditing({ ...editing, angle: e.target.value })} placeholder="Angle" aria-label="Angle" />
          <input className="text-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} aria-label="Title" />
          <input className="text-input" value={editing.dek} onChange={(e) => setEditing({ ...editing, dek: e.target.value })} aria-label="Dek" />
          <textarea className="text-input art-variant-body" value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} aria-label="Version" />
          <span className="art-row-actions">
            <button className="btn btn--primary btn--small" disabled={!!busy} onClick={() => act("save", async () => { if (await call(`${base}/variants/${editing.id}`, "PATCH", { angle: editing.angle, title: editing.title, dek: editing.dek, body: editing.body })) setEditing(null); })}>Save</button>
            <button className="btn btn--secondary btn--small" onClick={() => setEditing(null)}>Cancel</button>
          </span>
        </div>
      )}
      <div className="art-test__head">
        <h3 className="art-card__title">Results</h3>
        <select className="text-input art-ctx" value={ctx} onChange={(e) => setCtx(e.target.value)} aria-label="Neighborhood">
          <option value="">Citywide</option>
          {r.byNeighborhood.map((n) => <option key={n.context} value={n.context}>{n.name}</option>)}
        </select>
      </div>
      <div className="art-table-wrap">
        <table className="art-table">
          <thead><tr><th>Version</th><th>Readers</th><th>Engaged or converted</th><th>Chance it's best</th><th>Avg. time</th><th>Read to end</th><th>Ad seen</th><th>Conversions</th><th /></tr></thead>
          <tbody>{rows.map((a) => (
            <tr key={a.arm}>
              <td>{label(a.arm)}</td>
              <td>{a.impressions.toLocaleString()}</td>
              <td>{a.impressions ? `${pct(a.rate)} (${pct(a.low)}–${pct(a.high)})` : "—"}</td>
              <td>{a.impressions ? pct(a.probBest) : "—"}</td>
              <td>{a.impressions ? `${a.avgDwellSec}s` : "—"}</td>
              <td>{a.reads.toLocaleString()}</td>
              <td>{a.ad_views.toLocaleString()}</td>
              <td>{a.conversions.toLocaleString()}</td>
              <td>{r.winner !== a.arm && <button className="link-button" onClick={() => act("w", () => call(`${base}/winner`, "POST", { variant: a.arm }))}>Use for everyone</button>}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      {r.byNeighborhood.length > 0 && (
        <>
          <h3 className="art-card__title">Where each version leads</h3>
          <div className="art-table-wrap"><table className="art-table">
            <thead><tr><th>Neighborhood</th><th>Readers</th><th>Leading</th><th>Chance it's best</th><th>Leads with</th><th>Written for</th></tr></thead>
            <tbody>{r.byNeighborhood.map((n) => {
              const top = [...n.arms].sort((a, b) => b.probBest - a.probBest)[0];
              const v = r.variants.find((x) => x.id === top?.arm);
              return (
                <tr key={n.context}><td>{n.name}</td><td>{n.arms.reduce((t, a) => t + a.impressions, 0).toLocaleString()}</td><td>{top ? label(top.arm) : "—"}</td>
                  <td>{top ? pct(top.probBest) : "—"}</td><td>{v ? PERSPECTIVE_LABEL[v.perspective] : "The original"}</td><td>{v?.audience.join(", ") || "everyone"}</td></tr>
              );
            })}</tbody>
          </table></div>
        </>
      )}
      <p className="muted art-small">Each neighborhood starts from the citywide numbers and moves toward its own as readers there respond. One reader in ten gets a random version so the test keeps learning.</p>
    </section>
  );
}

/**
 * Search checks (Google's guidance for helpful content and passage ranking, block-admin.js STYLE): what search
 * results show and what readers searching for this story expect. Advice, not rules: publishing never waits on them.
 */
function SearchChecks({ d, nbs }: { d: Draft; nbs: Neighborhood[] }) {
  const names = d.neighborhoods.map((s) => nbs.find((n) => n.slug === s)?.name).filter(Boolean) as string[];
  const first = d.body.split(/\n\s*\n/).find((p) => p.trim() && !/^(#|>|\[\[|!\[)/.test(p.trim())) ?? "";
  const subheads = (d.body.match(/^## /gm) || []).length;
  const words = plainWords(d.body);
  const checks: [boolean, string][] = [
    [d.title.length > 0 && d.title.length <= 90, `Headline under 90 characters (${d.title.length})`],
    [d.dek.length > 0 && d.dek.length <= 160, `Dek under 160 characters, the search snippet (${d.dek.length})`],
    [!names.length || names.some((n) => `${d.title} ${first}`.includes(n)), "A neighborhood named in the headline or first paragraph"],
    [subheads >= 2, `Two or more subheads readers would search for (${subheads})`],
    [/\(\/matter\/[\w-]+\)/.test(d.body) || d.records.length > 0, "Linked to its legislation"],
    [/^> "/m.test(d.body), "Quotes someone on the record"],
    [words >= 950 && words <= 1100, `About 1,000 words, 950 to 1,100 (${words})`],
    [!!d.hero?.alt, "Image with alt text"],
  ];
  return (
    <ul className="art-checks" aria-label="Search checks">
      {checks.map(([ok, text]) => <li key={text} data-ok={ok || undefined}><span aria-hidden>{ok ? "✓" : "·"}</span>{text}</li>)}
    </ul>
  );
}
